import React, { useState, useEffect, useCallback } from 'react';
import ApiKeyModal from './components/ApiKeyModal';
import JsonEditor from './components/JsonEditor';
import SiteEditor from './components/SiteEditor';
import { MasterConfig as SiteConfig } from 'shared-types';

const BASE_URL = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '');

type AdminTab = 'ui' | 'raw';

const App: React.FC = () => {
    const [apiKey, setApiKey] = useState<string | null>(null);
    const [selectedSite, setSelectedSite] = useState<string>('teeznet');
    const [config, setConfig] = useState<SiteConfig | null>(null);
    const [rawConfig, setRawConfig] = useState('');
    const [activeTab, setActiveTab] = useState<AdminTab>('ui');
    
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    useEffect(() => {
        const storedKey = sessionStorage.getItem('adminApiKey');
        if (storedKey) setApiKey(storedKey);
    }, []);

    const fetchConfig = useCallback(async (siteId: string) => {
        if (!apiKey) return;
        setIsLoading(true); setError(null); setSuccess(null);
        try {
            const response = await fetch(`${BASE_URL}/api/config/live/${siteId}`);
            if (!response.ok) throw new Error(`Failed to fetch config for ${siteId}`);
            const data = await response.json();
            setConfig(data);
            setRawConfig(JSON.stringify(data, null, 2));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'An unknown error occurred.');
            setConfig(null);
            setRawConfig('');
        } finally {
            setIsLoading(false);
        }
    }, [apiKey]);

    useEffect(() => {
        if (apiKey) fetchConfig(selectedSite);
    }, [apiKey, selectedSite, fetchConfig]);

    const handleSave = async () => {
        if (!apiKey) return;

        let configToSave;
        try {
            configToSave = activeTab === 'ui' ? config : JSON.parse(rawConfig);
             if (!configToSave) throw new Error("Configuration is empty.");
        } catch (e) {
            setError("Error: Invalid JSON format. Please correct it before saving.");
            setSuccess(null);
            return;
        }

        setIsLoading(true); setError(null); setSuccess(null);
        try {
            const response = await fetch(`${BASE_URL}/api/config/save/${selectedSite}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-admin-api-key': apiKey, },
                body: JSON.stringify(configToSave, null, 2),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to save configuration.');
            setSuccess(data.message);
            // Refresh data after saving
            fetchConfig(selectedSite);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'An unknown error occurred.');
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleApiKeySubmit = (key: string) => {
        sessionStorage.setItem('adminApiKey', key);
        setApiKey(key);
    };

    if (!apiKey) {
        return <ApiKeyModal onSubmit={handleApiKeySubmit} />;
    }

    const handleConfigChange = (newConfig: SiteConfig) => {
        setConfig(newConfig);
        setRawConfig(JSON.stringify(newConfig, null, 2));
    };
    
    const handleRawConfigChange = (newRawConfig: string) => {
        setRawConfig(newRawConfig);
        try {
            const parsed = JSON.parse(newRawConfig);
            setConfig(parsed);
        } catch (e) {
            // It's invalid JSON, but we don't set an error until they try to save.
            // This allows them to continue typing.
        }
    };

    return (
        <div className="min-h-screen bg-slate-100 dark:bg-slate-900 text-slate-800 dark:text-slate-200">
            <header className="bg-white dark:bg-slate-800 shadow-md p-4 flex justify-between items-center">
                <h1 className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">Teez Platform Admin</h1>
                <div className="flex items-center gap-4">
                    {isLoading && (
                        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                            <span>Loading...</span>
                        </div>
                    )}
                    <button onClick={() => { setApiKey(null); sessionStorage.removeItem('adminApiKey'); }} className="text-sm font-medium text-slate-600 hover:text-red-500 dark:text-slate-300 dark:hover:text-red-400">
                        Logout
                    </button>
                </div>
            </header>
            
            <main className="p-4 sm:p-6 lg:p-8">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-8">
                    <aside className="lg:col-span-1">
                        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 sticky top-8">
                            <h2 className="text-lg font-semibold mb-4 border-b border-slate-200 dark:border-slate-700 pb-2">Select Site</h2>
                            <div className="space-y-2">
                                {/* Site selection logic */}
                                <button onClick={() => setSelectedSite('teeznet')} className={`w-full text-left flex items-center p-3 rounded-md cursor-pointer transition ${selectedSite === 'teeznet' ? 'bg-cyan-50 dark:bg-cyan-900/30' : 'hover:bg-slate-100 dark:hover:bg-slate-700/50'}`}>
                                    <span className="font-medium text-slate-800 dark:text-slate-200">TeezNet Site</span>
                                </button>
                                <button onClick={() => setSelectedSite('speedtest')} className={`w-full text-left flex items-center p-3 rounded-md cursor-pointer transition ${selectedSite === 'speedtest' ? 'bg-cyan-50 dark:bg-cyan-900/30' : 'hover:bg-slate-100 dark:hover:bg-slate-700/50'}`}>
                                    <span className="font-medium text-slate-800 dark:text-slate-200">Speed Test App</span>
                                </button>
                            </div>
                        </div>
                    </aside>
                    <div className="lg:col-span-3">
                        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md">
                           <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                               <div className="flex items-center gap-4">
                                   <div className="sm:hidden"><h2 className="text-lg font-bold">Config</h2></div>
                                   <div className="hidden sm:block"><h2 className="text-xl font-bold">Configuration: <span className="font-mono text-cyan-600 dark:text-cyan-400">{selectedSite}/master.json</span></h2></div>
                               </div>
                                <button onClick={handleSave} disabled={isLoading} className="px-6 py-2 bg-cyan-600 text-white font-semibold rounded-full hover:bg-cyan-500 transition disabled:opacity-50 disabled:cursor-wait">
                                    {isLoading ? 'Saving...' : 'Save Changes'}
                                </button>
                           </div>
                           <div className="border-b border-slate-200 dark:border-slate-700 px-4">
                               <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                                   <button onClick={() => setActiveTab('ui')} className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'ui' ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400' : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}>UI Editor</button>
                                   <button onClick={() => setActiveTab('raw')} className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'raw' ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400' : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}>Advanced (Raw JSON)</button>
                               </nav>
                           </div>
                           
                           <div className={activeTab === 'ui' ? '' : 'hidden'}>
                               {config ? <SiteEditor siteId={selectedSite} config={config} onChange={handleConfigChange} /> : <div className="p-8 text-center">Loading editor...</div>}
                           </div>

                           <div className={activeTab === 'raw' ? 'bg-[#1e1e1e] rounded-b-lg' : 'hidden'}>
                               <JsonEditor value={rawConfig} onChange={handleRawConfigChange} />
                           </div>
                        </div>
                        {error && <div className="mt-4 p-3 rounded-md bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm font-medium">{error}</div>}
                        {success && <div className="mt-4 p-3 rounded-md bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm font-medium">{success}</div>}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default App;